import { useState, useEffect, useReducer, useCallback, useRef } from 'react';
import axios from 'axios';
import { CheckCircle2, MapPin, AlertTriangle, Zap } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import BusinessSearchAutocomplete from '../components/BusinessSearchAutocomplete';
import COUNTRIES from '@/data/countries.json';
import SUBDIVISIONS from '@/data/subdivisions.json';
import CATEGORIES_DATA from '@/data/categories.json';
import useInferredCountry from '@/hooks/useInferredCountry';
import COUNTRY_REGIONS from '@/data/country_regions.json';

const API = '/api';

// Build country list from data file using Intl.DisplayNames
const buildCountryList = () => {
  let dn;
  try { dn = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { dn = null; }
  return Object.keys(COUNTRIES).map(code => ({
    code,
    name: (dn && dn.of(code)) || code,
  })).sort((a, b) => a.name.localeCompare(b.name));
};

const COUNTRY_LIST = buildCountryList();
const REGION_LIST = [...new Set(Object.values(COUNTRY_REGIONS))].sort();

const ALL_CATEGORIES_DATA = CATEGORIES_DATA.filter(c => c.slug);
const DEFAULT_CATEGORIES = ALL_CATEGORIES_DATA.map(c => c.name);
const OSM_NATIVE_CATEGORY_NAMES = new Set(
  ALL_CATEGORIES_DATA.filter(c => c.osm_native === true).map(c => c.name)
);

// ─── Form reducer ────────────────────────────────────────────────────────────

const INITIAL_FORM = {
  business_name: '',
  website: '',
  contact: '',
  email: '',
  service_region: '',
  based_state: '',
  country: 'AU',
  service_area: 'global',
  category: [],
  bitcoin_service: false,
  has_physical_location: null,
  notes: '',
  npub: '',
  payment_lightning: false,
  payment_lightning_contactless: false,
  payment_onchain: true,
  addr_housenumber: '',
  addr_street: '',
  addr_suburb: '',
  addr_city: '',
  addr_state: '',
  addr_postcode: '',
  full_address: '',
  latitude: null,
  longitude: null,
  // service scope for country branch
  service_scope_type: null,     // 'region' | 'country' | 'global'
  service_scope_value: '',      // ISO2 / region name / 'global'
  service_scope_state: '',      // ISO 3166-2 state code (country branch, physical)
};

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'MERGE':
      return { ...state, ...action.payload };
    case 'RESET':
      return { ...INITIAL_FORM };
    default:
      return state;
  }
}

// ─── Submit component ─────────────────────────────────────────────────────────

const Submit = () => {
  const { iso2: inferredIso2 } = useInferredCountry();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Dynamic lists from settings. Seeded minimally so the first-render
  // frame doesn't bias non-AU users with an Australia-only region list —
  // the real list populates when the settings fetch resolves.
  const [serviceRegions, setServiceRegions] = useState(['Global', 'Other']);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  // Main form state via reducer
  const [form, dispatch] = useReducer(formReducer, INITIAL_FORM);
  const formRef = useRef(form);
  formRef.current = form;
  const setField = useCallback((field, value) => dispatch({ type: 'SET_FIELD', field, value }), []);

  // Autocomplete / search state
  const [manualEntry, setManualEntry] = useState(false);
  const [pickedResult, setPickedResult] = useState(null);
  const [alreadyExists, setAlreadyExists] = useState(null);   // orangelabel duplicate
  const [btcmapHit, setBtcmapHit] = useState(false);          // picked from BTCMap
  const [editNotes, setEditNotes] = useState('');
  const [suggestEditSent, setSuggestEditSent] = useState(false);
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);

  // ISO 3166-2 states from backend (for service-scope country dropdown in online branch)
  const [countryStates, setCountryStates] = useState([]);

  // service_regions / categories came from /admin/general-settings before
  // the 2026-04-17 static-ref refactor — that endpoint requires admin auth
  // and anon submitters 401'd silently anyway. Initial state from the
  // static defaults is all this form needs.

  // Geo-infer country default
  useEffect(() => {
    if (inferredIso2) {
      const upper = inferredIso2.toUpperCase();
      if (COUNTRIES[upper]) {
        dispatch({ type: 'MERGE', payload: { country: upper } });
      }
    }
  }, [inferredIso2]);

  // Best-effort geolocation for autocomplete bias
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => { setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); },
      () => {},
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 600000 }
    );
  }, []);

  // Fetch ISO 3166-2 states when service-scope country changes (online branch)
  useEffect(() => {
    const iso2 = form.service_scope_type === 'country' ? form.service_scope_value : null;
    if (!iso2 || form.has_physical_location !== false) { setCountryStates([]); return; }
    axios.get(`${API}/public/country-states/${iso2}`)
      .then(r => setCountryStates(r.data?.states || []))
      .catch(() => setCountryStates([]));
  }, [form.service_scope_value, form.service_scope_type, form.has_physical_location]);

  // ─── Autocomplete pick handler ──────────────────────────────────────────────

  const handleAutocompletePick = useCallback((result) => {
    setPickedResult(result);
    const f = formRef.current;

    // Already in Orange Label
    if (result.source === 'orangelabel' && result.already_listed) {
      setAlreadyExists(result);
      return;
    }

    // From BTCMap — pre-fill + show banner, but still allow adding to OL
    if (result.source === 'btcmap') {
      setBtcmapHit(true);
    } else {
      setBtcmapHit(false);
    }

    setManualEntry(true);
    dispatch({
      type: 'MERGE',
      payload: {
        business_name: result.name || f.business_name,
        website: result.website || f.website,
        contact: result.phone || f.contact,
        addr_housenumber: result.addr_housenumber || f.addr_housenumber,
        addr_street: result.addr_street || f.addr_street,
        addr_suburb: result.addr_suburb || f.addr_suburb,
        addr_city: result.addr_city || f.addr_city,
        addr_state: result.addr_state || f.addr_state,
        addr_postcode: result.addr_postcode || f.addr_postcode,
        full_address: result.full_address || f.full_address,
        country: result.country_code || f.country,
        latitude: result.latitude ?? f.latitude,
        longitude: result.longitude ?? f.longitude,
        category: result.category_guess && OSM_NATIVE_CATEGORY_NAMES.has(result.category_guess)
          ? Array.from(new Set([...(f.category || []), result.category_guess]))
          : f.category,
      },
    });
    const src = result.source === 'btcmap' ? 'BTCMap' : result.source === 'photon' ? 'OpenStreetMap' : 'directory';
    toast.success(`Auto-filled from ${src}`);
  }, []);

  // ─── Suggest edit ───────────────────────────────────────────────────────────

  const handleSuggestEdit = async () => {
    if (!alreadyExists || sending) return;
    const changes = {};
    if (form.website) changes.website = form.website;
    if (form.contact) changes.contact = form.contact;
    if (form.email) changes.email = form.email;
    if (form.notes) changes.notes = form.notes;
    if (Object.keys(changes).length === 0 && !editNotes) {
      toast.error('Add at least one field change or note before suggesting an edit.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/businesses/${alreadyExists.id}/suggest-edit`, {
        changes, notes: editNotes, submitter_email: form.email,
      });
      setSuggestEditSent(true);
      toast.success('Suggested edit submitted for review. Thanks!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit edit suggestion');
    } finally {
      setSending(false);
    }
  };

  // ─── Category toggle ────────────────────────────────────────────────────────

  const handleCategoryToggle = useCallback((category) => {
    dispatch({
      type: 'MERGE',
      payload: {
        category: form.category.includes(category)
          ? form.category.filter(c => c !== category)
          : [...form.category, category],
      },
    });
  }, [form.category]);

  // ─── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.business_name) { toast.error('Business name is required'); return; }
    if (form.has_physical_location === true && !form.addr_state && !form.based_state) {
      toast.error('Please select your state / province'); return;
    }
    if (form.category.length === 0) { toast.error('Please select at least one category'); return; }
    if (form.has_physical_location === null) {
      toast.error('Please indicate if you have a physical location'); return;
    }
    if (form.has_physical_location === false) {
      if (!form.service_scope_type) {
        toast.error('Please choose what region you service'); return;
      }
      if (form.service_scope_type !== 'global' && !form.service_scope_value) {
        toast.error('Please select a region or country'); return;
      }
    }

    setLoading(true);
    try {
      const effectiveScopeValue = form.service_scope_value || form.service_area || 'global';
      const submitData = {
        business_name: form.business_name,
        website: form.website,
        contact: form.contact,
        // IMPORTANT: the form's single email field captures the SUBMITTER's
        // contact email (so we can reach them about the submission). It is
        // NEVER written to the business record's public `email` field, which
        // would leak a personal address to OpenStreetMap as `contact:email`.
        // The business's public contact email is only populated if the
        // business itself explicitly provides one (future UX).
        submitter_email: form.email,
        region: form.service_region || effectiveScopeValue,
        based_state: form.based_state || form.addr_state,
        service_area: form.has_physical_location === false ? effectiveScopeValue : undefined,
        addr_country: form.country,
        category: form.category,
        bitcoin_service: form.bitcoin_service,
        has_physical_location: form.has_physical_location,
        notes: form.notes,
        npub: form.npub,
        listing_destination: form.has_physical_location ? 'btcmap' : 'orangelabel',
        payment_lightning: form.payment_lightning,
        payment_lightning_contactless: form.payment_lightning_contactless,
        payment_onchain: form.payment_onchain,
        addr_housenumber: form.addr_housenumber,
        addr_street: form.addr_street,
        addr_suburb: form.addr_suburb,
        addr_city: form.addr_city,
        addr_state: form.addr_state || form.based_state,
        addr_postcode: form.addr_postcode,
        full_address: form.full_address,
        latitude: form.latitude,
        longitude: form.longitude,
        source: pickedResult ? pickedResult.source : 'manual',
        source_id: pickedResult ? pickedResult.id : undefined,
        // Photon returns osm_id as a number; backend Pydantic wants string.
        osm_id: pickedResult && pickedResult.osm_id != null
          ? String(pickedResult.osm_id)
          : undefined,
        osm_type: pickedResult ? (pickedResult.osm_type || undefined) : undefined,
        btcmap_id: pickedResult?.btcmap_id || undefined,
        // Flag: this was already on BTCMap → OL only, no re-submit to OSM
        btcmap_already_listed: btcmapHit || undefined,
      };

      const response = await axios.post(`${API}/businesses`, submitData);
      if (response.data.duplicate_warning) {
        toast.warning(response.data.duplicate_warning, { duration: 6000 });
      }
      setSubmitted(true);
      toast.success('Business submitted successfully!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit business. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Reset ───────────────────────────────────────────────────────────────────

  const resetForm = () => {
    setSubmitted(false);
    dispatch({ type: 'RESET' });
    setManualEntry(false);
    setPickedResult(null);
    setAlreadyExists(null);
    setBtcmapHit(false);
    setEditNotes('');
    setSuggestEditSent(false);
    setCountryStates([]);
  };

  // ─── Subdivision helpers ─────────────────────────────────────────────────────

  const getSubdivisions = (countryCode) => SUBDIVISIONS[countryCode] || null;

  // ─── Success screen ──────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-2xl mx-auto text-center" role="status">
          <CheckCircle2 className="w-12 sm:w-16 h-12 sm:h-16 text-success mx-auto mb-4 sm:mb-6" strokeWidth={1.5} />
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-3 sm:mb-4" data-testid="success-heading">
            Thanks for Your Submission!
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
            Your Bitcoin-accepting business has been submitted and will appear in the directory once approved.
          </p>
          <button
            onClick={resetForm}
            className="bg-primary text-white hover:bg-primary-hover rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-semibold transition-all shadow-sm hover:shadow-md text-sm sm:text-base"
            data-testid="submit-another-btn"
          >
            Submit Another Business
          </button>
        </div>
      </div>
    );
  }

  // Derived booleans for readability
  const isPhysical = form.has_physical_location === true;
  const isOnline = form.has_physical_location === false;
  const physicalSearchDone = isPhysical && (manualEntry || alreadyExists);
  // Show rest of form when: online + name filled OR physical + search done
  const showRestOfForm = (isOnline && form.business_name.trim()) || (isPhysical && physicalSearchDone && !alreadyExists);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-bold font-heading text-foreground mb-3 sm:mb-4" data-testid="submit-heading">
            List Your Business
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Add your Bitcoin-accepting business to the directory
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">

          {/* ── STEP 1 — Physical location Y/N ── */}
          <div className="space-y-2">
            <Label>Do customers visit a fixed address? *</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Pick "Yes" only if customers come to you at a fixed premises. If you travel to the customer (plumber, electrician, mobile mechanic) or work purely online, pick "No". All businesses are listed on Orange Label; fixed-premises ones are also listed on BTCMap.org.
            </p>
            <div className="flex flex-col sm:flex-row gap-3" data-testid="step1-physical-choice">
              <button
                type="button"
                onClick={() => {
                  dispatch({ type: 'MERGE', payload: {
                    has_physical_location: true,
                    category: form.category.filter(c => OSM_NATIVE_CATEGORY_NAMES.has(c)),
                  }});
                  setManualEntry(false);
                  setPickedResult(null);
                  setAlreadyExists(null);
                  setBtcmapHit(false);
                }}
                className={`flex-1 border rounded-lg p-4 text-left transition-all ${
                  isPhysical
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-border hover:border-muted-foreground'
                }`}
                data-testid="step1-yes-physical"
              >
                <div className="font-semibold text-sm mb-1">Yes — customers come to us</div>
                <div className="text-xs text-muted-foreground">Fixed premises customers visit (cafe, shop, office)</div>
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Listed on BTCMap + Orange Label</div>
              </button>
              <button
                type="button"
                onClick={() => {
                  dispatch({ type: 'MERGE', payload: { has_physical_location: false }});
                  setManualEntry(false);
                  setPickedResult(null);
                  setAlreadyExists(null);
                  setBtcmapHit(false);
                }}
                className={`flex-1 border rounded-lg p-4 text-left transition-all ${
                  isOnline
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : 'border-border hover:border-muted-foreground'
                }`}
                data-testid="step1-no-online"
              >
                <div className="font-semibold text-sm mb-1">No — online or mobile service</div>
                <div className="text-xs text-muted-foreground">We travel to the customer or work online (plumber, electrician, handyman, e-commerce)</div>
                <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">Listed on Orange Label</div>
              </button>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* BRANCH A — Physical                                               */}
          {/* ══════════════════════════════════════════════════════════════════ */}

          {/* Step 2a — Find your business (autocomplete) */}
          {isPhysical && !manualEntry && !alreadyExists && (
            <div className="space-y-2">
              <Label>Find your business</Label>
              <p className="text-xs text-muted-foreground">
                Search Orange Label, BTCMap, and OpenStreetMap to find your existing listing, or enter manually.
              </p>
              <BusinessSearchAutocomplete
                lat={userLat}
                lng={userLng}
                onPick={handleAutocompletePick}
                onManualEntry={() => { setManualEntry(true); setPickedResult(null); }}
              />
            </div>
          )}

          {/* Already exists in Orange Label */}
          {alreadyExists && (
            <div className="border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-3" data-testid="already-exists-block">
              <div>
                <div className="font-semibold text-base">This business already exists in our directory</div>
                <div className="text-sm text-muted-foreground">{alreadyExists.name}</div>
                {alreadyExists.full_address && (
                  <div className="text-xs text-muted-foreground">{alreadyExists.full_address}</div>
                )}
              </div>
              {!suggestEditSent ? (
                <>
                  <p className="text-xs">
                    You can <a className="text-primary underline" href={`/directory?search=${encodeURIComponent(alreadyExists.name)}`} target="_blank" rel="noopener noreferrer">view the existing record</a>, or suggest an edit below.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input placeholder="Website (optional change)" value={form.website} onChange={(e) => setField('website', e.target.value)} />
                    <Input placeholder="Phone (optional change)" value={form.contact} onChange={(e) => setField('contact', e.target.value)} />
                  </div>
                  <Input placeholder="Email (optional)" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                  <Textarea placeholder="Notes for the reviewer" rows={3} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                  <div className="flex gap-2">
                    <button type="button" onClick={handleSuggestEdit} disabled={sending} className="bg-primary text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="suggest-edit-btn">
                      {sending ? 'Submitting…' : 'Submit suggested edit'}
                    </button>
                    <button type="button" onClick={() => { setAlreadyExists(null); setPickedResult(null); }} className="border border-border rounded-full px-4 py-2 text-sm">
                      Back to search
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Edit suggestion sent. Thanks!
                </div>
              )}
            </div>
          )}

          {/* BTCMap banner */}
          {isPhysical && manualEntry && btcmapHit && (
            <div className="border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 flex gap-3" data-testid="btcmap-banner">
              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-semibold">This business is already on BTCMap.</span>
                {' '}We'll add it to the Orange Label directory only — no duplicate OSM submission.
              </div>
            </div>
          )}

          {/* Physical: manual entry form - Step 3a address */}
          {isPhysical && manualEntry && !alreadyExists && (
            <>
              {/* Business name pre-filled or editable */}
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  type="text"
                  required
                  maxLength={200}
                  value={form.business_name}
                  onChange={(e) => setField('business_name', e.target.value)}
                  className="h-12"
                  data-testid="business-name-input"
                />
              </div>

              {/* Step 3a — Service region */}
              <div className="space-y-2">
                <Label htmlFor="service_region">Service Region *</Label>
                <Select value={form.service_region} onValueChange={(v) => setField('service_region', v)}>
                  <SelectTrigger className="h-12 bg-card" data-testid="service-region-select">
                    <SelectValue placeholder="Select service region" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceRegions.map(region => (
                      <SelectItem key={region} value={region}>{region}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Defaults to your physical address state, but can be changed
                </p>
              </div>

              {/* Address block */}
              <div className="space-y-4 border border-border rounded-lg p-4">
                <Label className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4" />
                  Physical Address
                </Label>

                <div>
                  <Label htmlFor="addr_country" className="text-xs text-muted-foreground">Country *</Label>
                  <Select
                    value={form.country}
                    onValueChange={(value) => dispatch({ type: 'MERGE', payload: { country: value, based_state: '', addr_state: '' } })}
                  >
                    <SelectTrigger className="h-12 bg-card" data-testid="country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {COUNTRY_LIST.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="addr_state" className="text-xs text-muted-foreground">State / Province *</Label>
                  {getSubdivisions(form.country) ? (
                    <Select
                      value={form.addr_state || form.based_state}
                      onValueChange={(value) => dispatch({ type: 'MERGE', payload: {
                        addr_state: value,
                        based_state: value,
                        service_region: form.service_region || value,
                      }})}
                    >
                      <SelectTrigger data-testid="based-state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {getSubdivisions(form.country).map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="addr_state"
                      placeholder="State or province"
                      value={form.addr_state || form.based_state}
                      onChange={(e) => dispatch({ type: 'MERGE', payload: { addr_state: e.target.value, based_state: e.target.value } })}
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Street Number</Label>
                    <Input placeholder="e.g., 123" value={form.addr_housenumber} onChange={(e) => setField('addr_housenumber', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Street Name</Label>
                    <Input placeholder="e.g., Main Street" value={form.addr_street} onChange={(e) => setField('addr_street', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Suburb</Label>
                    <Input placeholder="e.g., Surry Hills" value={form.addr_suburb} onChange={(e) => setField('addr_suburb', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">City</Label>
                    <Input placeholder="e.g., Sydney" value={form.addr_city} onChange={(e) => setField('addr_city', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Postcode</Label>
                    <Input placeholder="e.g., 2000" value={form.addr_postcode} onChange={(e) => setField('addr_postcode', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Or Full Address</Label>
                  <Input
                    placeholder="e.g., 123 Main Street, Surry Hills NSW 2000"
                    value={form.full_address}
                    onChange={(e) => setField('full_address', e.target.value)}
                  />
                </div>
              </div>

              {/* "Change search" escape hatch */}
              <button
                type="button"
                onClick={() => { setManualEntry(false); setPickedResult(null); setBtcmapHit(false); }}
                className="text-xs text-primary underline"
                data-testid="back-to-search"
              >
                ← Back to search
              </button>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* BRANCH B — Online only                                            */}
          {/* ══════════════════════════════════════════════════════════════════ */}

          {/* Step 2b — Business name (plain text) */}
          {isOnline && (
            <div className="space-y-2">
              <Label htmlFor="business_name_online">Business Name *</Label>
              <Input
                id="business_name_online"
                type="text"
                required
                maxLength={200}
                value={form.business_name}
                onChange={(e) => setField('business_name', e.target.value)}
                className="h-12"
                data-testid="business-name-input"
                placeholder="Your business name"
              />
            </div>
          )}

          {/* Step 3b — Service region (online) */}
          {isOnline && form.business_name.trim() && (
            <>
              <div className="space-y-3" data-testid="service-scope-block">
                <Label>What region do you service? *</Label>
                <p className="text-xs text-muted-foreground">
                  Choose whether you service a whole region, a specific country, or worldwide.
                </p>
                <div className="flex gap-3">
                  {[
                    { key: 'region', label: 'A region', sub: 'e.g. Oceania, Europe…' },
                    { key: 'country', label: 'A country', sub: 'Select a specific country' },
                    { key: 'global', label: 'Global', sub: 'Worldwide / no restriction' },
                  ].map(({ key, label, sub }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        const val = key === 'global' ? 'global' : '';
                        dispatch({ type: 'MERGE', payload: {
                          service_scope_type: key,
                          service_scope_value: val,
                          service_scope_state: '',
                          service_area: val || 'global',
                        }});
                      }}
                      data-testid={`scope-type-${key}`}
                      className={`flex-1 border rounded-lg p-3 text-left transition-all ${
                        form.service_scope_type === key
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                          : 'border-border hover:border-muted-foreground bg-card'
                      }`}
                    >
                      <div className="font-semibold text-sm">{label}</div>
                      <div className="text-xs text-muted-foreground">{sub}</div>
                    </button>
                  ))}
                </div>

                {form.service_scope_type === 'region' && (
                  <Select
                    value={form.service_scope_value}
                    onValueChange={(v) => dispatch({ type: 'MERGE', payload: { service_scope_value: v, service_area: v } })}
                  >
                    <SelectTrigger className="h-12 bg-card" data-testid="scope-region-select">
                      <SelectValue placeholder="Select a region" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGION_LIST.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {form.service_scope_type === 'country' && (
                  <>
                    <Select
                      value={form.service_scope_value}
                      onValueChange={(code) => dispatch({ type: 'MERGE', payload: {
                        service_scope_value: code,
                        service_area: code,
                        service_scope_state: '',
                        country: code,
                      }})}
                    >
                      <SelectTrigger className="h-12 bg-card" data-testid="scope-country-select">
                        <SelectValue placeholder="Select a country" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COUNTRY_LIST.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* State dropdown — shown after country picked */}
                    {form.service_scope_value && (
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          State / Province (optional)
                        </Label>
                        {countryStates.length > 0 ? (
                          <Select
                            value={form.service_scope_state || '__national__'}
                            onValueChange={(v) => dispatch({ type: 'MERGE', payload: {
                              service_scope_state: v === '__national__' ? '' : v,
                              based_state: v === '__national__' ? '' : v,
                            }})}
                            data-testid="scope-state-select"
                          >
                            <SelectTrigger className="h-12 bg-card">
                              <SelectValue placeholder="National Service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__national__">National Service</SelectItem>
                              {countryStates.map(s => (
                                <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            placeholder="State or province (optional)"
                            value={form.service_scope_state}
                            onChange={(e) => dispatch({ type: 'MERGE', payload: {
                              service_scope_state: e.target.value,
                              based_state: e.target.value,
                            }})}
                            className="h-12"
                            data-testid="scope-state-input"
                          />
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave as "National Service" if you serve the whole country.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* STEP 4 — Common fields (gated)                                    */}
          {/* ══════════════════════════════════════════════════════════════════ */}

          {showRestOfForm && (
            <>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="text"
                  maxLength={500}
                  placeholder="example.com or https://example.com"
                  value={form.website}
                  onChange={(e) => setField('website', e.target.value)}
                  className="h-12"
                  data-testid="website-input"
                />
                <p className="text-xs text-muted-foreground">Will be formatted automatically</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact">Contact Phone</Label>
                  <Input
                    id="contact"
                    type="text"
                    placeholder="0412 345 678"
                    value={form.contact}
                    onChange={(e) => setField('contact', e.target.value)}
                    className="h-12"
                    data-testid="contact-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email (optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    className="h-12"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>
                  {isPhysical
                    ? 'Category * (OSM-compatible, select all that apply)'
                    : 'Category * (Select all that apply)'}
                </Label>
                {isPhysical && (
                  <p className="text-xs text-muted-foreground">
                    Physical locations are submitted to BTCMap and must use OSM-compatible categories.
                  </p>
                )}
                <div className="grid grid-cols-1 gap-2 sm:gap-3 border border-border rounded-md p-3 sm:p-4 max-h-64 overflow-y-auto" data-testid="category-list">
                  {categories
                    .filter(c => isPhysical ? OSM_NATIVE_CATEGORY_NAMES.has(c) : true)
                    .map((category) => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox
                          id={`category-${category}`}
                          checked={form.category.includes(category)}
                          onCheckedChange={() => handleCategoryToggle(category)}
                          data-testid={`category-checkbox-${category}`}
                        />
                        <label htmlFor={`category-${category}`} className="text-sm font-medium cursor-pointer">
                          {category}
                        </label>
                      </div>
                    ))}
                </div>
              </div>

              {/* Bitcoin Payment Methods */}
              <div className="space-y-4 border border-border rounded-lg p-4">
                <div>
                  <Label className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4" />
                    Bitcoin Payment Methods
                  </Label>
                  <p className="text-xs text-muted-foreground mb-4">
                    Select how customers can pay with Bitcoin at your business.
                  </p>
                  <div className="space-y-3">
                    {[
                      { id: 'payment_onchain', label: 'On-chain Bitcoin', desc: 'Traditional Bitcoin payments via QR code' },
                      { id: 'payment_lightning', label: 'Lightning Network', desc: 'Instant payments via Lightning invoice' },
                      { id: 'payment_lightning_contactless', label: 'Lightning NFC/Tap-to-Pay', desc: 'Contactless Lightning payments via NFC cards' },
                    ].map(({ id, label, desc }) => (
                      <div key={id} className="flex items-start space-x-3 p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                        <Checkbox
                          id={id}
                          checked={form[id]}
                          onCheckedChange={(c) => setField(id, c === true)}
                        />
                        <div className="flex-1">
                          <label htmlFor={id} className="text-sm font-medium cursor-pointer block">{label}</label>
                          <p className="text-xs text-muted-foreground">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t border-border pt-4 sm:pt-6">
                <Label>Optional Information</Label>

                <div className="space-y-2">
                  <Label htmlFor="npub">NOSTR Public Key (npub)</Label>
                  <Input
                    id="npub"
                    type="text"
                    placeholder="npub1..."
                    value={form.npub}
                    onChange={(e) => setField('npub', e.target.value)}
                    className="h-12 font-mono text-sm"
                    data-testid="npub-input"
                  />
                  <p className="text-xs text-muted-foreground">Your NOSTR public key for decentralized social engagement</p>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bitcoin_service"
                    checked={form.bitcoin_service}
                    onCheckedChange={(c) => setField('bitcoin_service', c === true)}
                    data-testid="bitcoin-service-checkbox"
                  />
                  <label htmlFor="bitcoin_service" className="text-sm font-medium cursor-pointer">
                    Bitcoin, or Bitcoin &amp; Stablecoins
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes / Description (max 400 characters)</Label>
                <Textarea
                  id="notes"
                  maxLength={400}
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="Any additional information about your business..."
                  data-testid="notes-textarea"
                />
                <p className="text-xs text-muted-foreground">{form.notes.length}/400</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-4 font-semibold transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-business"
              >
                {loading ? 'Submitting...' : 'Submit Business'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Submit;
